import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useIsMobile } from '@/lib/utils/useIsMobile';

/**
 * <b>모바일의 모든 고르는 칸</b>을 바텀시트로 띄운다 (사용자 지시 2026-08-31).
 *
 * ## 왜 화면마다 바꾸지 않았나
 * 이 앱의 {@code <select>} 는 예순 곳이 넘는다. 하나씩 전용 컴포넌트로 갈아 끼우면 <b>반드시
 * 빠뜨리고</b>, 빠뜨린 칸은 그 화면에서만 브라우저 기본 고르개로 떠서 "여기만 왜 다르지" 가 된다.
 * 실제로 공지 분류는 바꿨는데 바로 옆의 정렬 칸이 그대로였다(사용자 지적).
 *
 * <p>그래서 <b>자리를 하나로 모은다</b>. 화면 전체에서 고르는 칸 누름을 가로채고, 그 칸이 들고
 * 있는 {@code <option>} 을 그대로 읽어 시트를 세운다. 화면 쪽 코드는 <b>아무것도 바꾸지 않는다</b> —
 * 앞으로 새로 만드는 칸도 저절로 같은 모양이 된다.
 *
 * ## 값을 되돌려 주는 방법
 * React 가 관리하는 {@code value} 는 그냥 {@code el.value = …} 로 바꾸면 <b>React 가 모른다</b>
 * (자기가 마지막에 쓴 값과 같다고 여겨 다시 그리지 않는다). 그래서 프로토타입의 setter 를 직접
 * 불러 값을 넣고, 진짜 {@code change} 이벤트를 쏘아 준다 — 그러면 화면이 평소처럼 반응한다.
 *
 * ## 손대지 않는 것
 * <ul>
 *   <li><b>여러 줄 목록</b>({@code multiple}·{@code size}) — 시트로 표현할 모양이 아니다.</li>
 *   <li><b>꺼진 칸</b>({@code disabled}) — 눌러도 아무 일이 없어야 한다.</li>
 *   <li><b>키보드 조작</b> — 막지 않는다. 스페이스·화살표로 여는 길은 그대로 둔다(가로챈 것은 누름뿐).</li>
 *   <li><b>웹</b> — 767px 을 넘으면 이 컴포넌트는 아무 일도 하지 않는다.</li>
 * </ul>
 */
export function NativeSelectSheet() {
  const isMobile = useIsMobile();
  const [target, setTarget] = useState<HTMLSelectElement | null>(null);
  /*
    ★ 여는 누름에 딸려 오는 click 한 번을 삼킨다 (사용자 지적 2026-08-31 · 「눌렀는데 바로 닫힌다」).

    한 번의 누름은 `pointerdown → (pointerup) → click` 세 벌로 온다. 시트는 첫 벌에서 뜨므로,
    마지막 click 이 도착할 때 <b>손가락(커서) 밑에는 이미 시트의 뒷막</b>이 있다. 그 뒷막의
    「바깥을 누르면 닫는다」 가 곧바로 걸려 열리자마자 닫혔다.

    <p>아래 capture 단계 handler 가 그 한 벌만 먹어 치운다. 두 번째 click 부터는 평소대로 —
    바깥을 누르면 닫힌다.
  */
  const swallowNextClick = useRef(false);

  useEffect(() => {
    if (!isMobile) {
      setTarget(null);
      return;
    }

    /*
      `pointerdown` 을 <b>가로채는 단계</b>에서 잡는다. 브라우저는 이 시점 이후에 자기 고르개를
      띄우므로, 여기서 막지 않으면 우리 시트와 기본 고르개가 <b>둘 다</b> 뜬다.
    */
    const onPointerDown = (event: PointerEvent) => {
      const node = event.target as Element | null;
      const select = node?.closest?.('select') as HTMLSelectElement | null;
      // 고르는 칸이 아닌 곳을 누르면 「삼킬 click」 은 없다. 끌다 만 누름 때문에 표시가 남아
      // 다음에 엉뚱한 누름을 먹는 일을 막는다.
      if (!select || select.disabled || select.multiple || select.size > 1) {
        swallowNextClick.current = false;
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      swallowNextClick.current = true;
      setTarget(select);
    };

    // 누름을 막아도 일부 브라우저는 뒤이어 click 을 낸다 — 그때 다시 열리지 않게 함께 막는다.
    const onClick = (event: MouseEvent) => {
      // 여는 누름의 꼬리다. 시트 뒷막까지 가면 열리자마자 닫힌다.
      if (swallowNextClick.current) {
        swallowNextClick.current = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const node = event.target as Element | null;
      const select = node?.closest?.('select') as HTMLSelectElement | null;
      if (!select || select.disabled || select.multiple || select.size > 1) return;
      event.preventDefault();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('click', onClick, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('click', onClick, true);
    };
  }, [isMobile]);

  useEffect(() => {
    if (!target) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setTarget(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [target]);

  if (!target) return null;

  const options = Array.from(target.options);
  /*
    시트 제목 — 「무엇을 고르는 중인가」. 화면이 붙여 둔 이름표를 그대로 쓴다. 셋 다 없으면
    「선택」 으로 둔다. 제목을 지어내지 않는다: 틀린 제목은 없는 제목보다 나쁘다.
  */
  const title =
    target.getAttribute('aria-label')
    ?? (target.labels?.[0]?.textContent?.trim() || null)
    ?? target.getAttribute('title')
    ?? '선택';

  const pick = (value: string) => {
    /*
      React 가 값이 바뀐 것을 알아채게 하는 유일한 길이다. 프로토타입의 setter 를 직접 부르면
      React 가 심어 둔 «마지막 값» 추적을 건드리지 않고 실제 DOM 값만 바뀌고, 이어서 쏘는
      change 이벤트에서 React 가 «달라졌다» 고 판단한다.
    */
    const setter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set;
    if (setter) setter.call(target, value);
    else target.value = value;

    target.dispatchEvent(new Event('change', { bubbles: true }));
    setTarget(null);
  };

  return createPortal(
    <div className="select-sheet-backdrop" role="presentation" onClick={() => setTarget(null)}>
      {/* 시트 안쪽 누름은 삼킨다 — 고르려다 여백에 닿아 닫히면 처음부터 다시 열어야 한다. */}
      <div
        className="select-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="select-sheet-grip" aria-hidden />
        <p className="select-sheet-title">{title}</p>

        <div className="select-sheet-list" role="listbox" aria-label={title}>
          {options.map((option) => {
            const active = option.value === target.value;
            return (
              <button
                key={`${option.value}-${option.index}`}
                type="button"
                role="option"
                aria-selected={active}
                disabled={option.disabled}
                className={`select-sheet-item${active ? ' active' : ''}`}
                onClick={() => pick(option.value)}
              >
                <span className="truncate">{option.textContent}</span>
                {active && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}
